import AddUsersDialog from "@/components/ProjectRoleEditor/components/AddUsersDialog";
import { CONTENT_WIDTH, GUTTER_SIZE } from "@/enums/";
import { useToggle } from "@/hooks/useToggle";
import { IAvailableUser, ITranslation, IUser } from "@/interfaces";
import { useGetFormLookupsQuery } from "@/services";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCirclePlus,
  faTrash,
  faUsers,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, Col, Form, Row, Select, Space, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const { Option } = Select;

interface IProps {
  isLoading: boolean;
  availableUsers: IAvailableUser[];
  setAvailableUsers: (availableUsers: IAvailableUser[]) => void;
  isLimitedList: boolean;
}

export const UserList = ({
  isLoading,
  availableUsers,
  setAvailableUsers,
  isLimitedList,
}: IProps) => {
  const intl = useIntl();

  const [showAddUsersDialog, toggleShowAddUsersDialog] = useToggle(false);
  const [productCategories, setProductCategories] = useState<{
    [id: string]: ITranslation;
  }>({});

  const { data: { intake } = {} } = useGetFormLookupsQuery();

  useEffect(() => {
    if (!intake) return;

    setProductCategories(intake.productCategory);
  }, [intake]);

  const setItemDeleted = (item: string): void => {
    setAvailableUsers([
      ...availableUsers.filter(({ user: { id } }) => id !== item),
    ]);
  };

  const addUsersClicked = (): void => {
    toggleShowAddUsersDialog();
  };

  const addUsers = (users: IUser[]): void => {
    const newUsers: IAvailableUser[] = users
      .filter(
        (newUser) => !availableUsers.some(({ user }) => user.id === newUser.id)
      )
      .map((user) => {
        const newAvailableUser: IAvailableUser = {
          key: user.id,
          user: {
            ...user,
            fullName: user.displayName,
          },
          isNew: true,
          productCategoriesDefaultFor: [],
          productCategoryFilters: [],
          isDeleted: false,
          isInactiveInAzure: false,
        };
        return newAvailableUser;
      });

    setAvailableUsers([...availableUsers, ...newUsers]);
  };

  const columns: ColumnsType<IAvailableUser> = [
    {
      title: intl.formatMessage({ id: "projectRoles.colName" }),
      dataIndex: ["user", "fullName"],
      key: "userName",
    },
  ];

  if (isLimitedList) {
    columns.push({
      title: intl.formatMessage({
        id: "projectRoles.colProductCategoryFilter",
      }),
      key: "productCategoryFilters",
      width: "29%",
      render: (_, { productCategoryFilters, user: { id } }) => (
        <Form.Item
          name={`${id}-productCategoryFilters`}
          initialValue={productCategoryFilters}
          className="m-0"
        >
          <Select
            data-test-id="template-role-editor-limited-list-default"
            mode="multiple"
          >
            {productCategories &&
              Object.keys(productCategories).map((key) => (
                <Option key={key} value={key}>
                  {productCategories[key]?.fallback}
                </Option>
              ))}
          </Select>
        </Form.Item>
      ),
    });
  }

  columns.push({
    title: intl.formatMessage({ id: "projectRoles.colDefaultFor" }),
    key: "defaultFor",
    width: "48%",
    render: (_, { productCategoriesDefaultFor, user: { id } }) => (
      <Form.Item
        name={`${id}-productCategoriesDefaultFor`}
        initialValue={productCategoriesDefaultFor}
        rules={[
          {
            validator: async (_, values): Promise<void> => {
              if (!isLimitedList && !values?.length) {
                return Promise.reject(
                  new Error(
                    intl.formatMessage({
                      id: "projectRoles.defaultIsRequiredError",
                    })
                  )
                );
              }

              const otherUserValues = availableUsers
                .filter((availableUser) => availableUser.user.id !== id)
                .map(
                  ({ productCategoriesDefaultFor }) =>
                    productCategoriesDefaultFor
                )
                .flat();

              const hasDuplicateCategories = values.some((value: string) =>
                otherUserValues?.includes(value)
              );
              if (hasDuplicateCategories) {
                return Promise.reject(
                  new Error(
                    intl.formatMessage({
                      id: "projectRoles.defaultForError",
                    })
                  )
                );
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <Select
          data-test-id="template-role-editor-limited-list-default"
          mode="multiple"
        >
          {productCategories &&
            Object.keys(productCategories).map((key) => (
              <Option key={key} value={key}>
                {productCategories[key]?.fallback}
              </Option>
            ))}
        </Select>
      </Form.Item>
    ),
  });

  columns.push({
    title: intl.formatMessage({ id: "general.actions" }),
    key: "Actions",
    width: "75px",
    align: "center",
    render: (_, { key }) => (
      <Button
        type="link"
        icon={<FontAwesomeIcon icon={faTrash as IconProp} />}
        data-test-id={`template-role-editor-delete-${key}`}
        onClick={(): void => setItemDeleted(key)}
      />
    ),
  });

  return (
    <>
      <Row gutter={GUTTER_SIZE.large}>
        <Col span={CONTENT_WIDTH.max}>
          <Card
            style={{ height: "auto", marginBottom: "12px" }}
            loading={isLoading}
            title={
              <Space>
                <FontAwesomeIcon icon={faUsers as IconProp} />
                <span>{intl.formatMessage({ id: "projectRoles.users" })}</span>
              </Space>
            }
            extra={
              <Button
                data-test-id="template-role-editor-add-user"
                type="primary"
                style={{ float: "right" }}
                icon={<FontAwesomeIcon icon={faCirclePlus as IconProp} />}
                onClick={addUsersClicked}
                disabled={
                  !isLimitedList &&
                  availableUsers.length >=
                    (productCategories && Object.keys(productCategories).length)
                }
              >
                {intl.formatMessage({ id: "projectRoles.addUsers" })}
              </Button>
            }
          >
            {!!availableUsers.length && (
              <Card.Grid
                hoverable={false}
                style={{ width: "100%", height: "100%" }}
              >
                <Table
                  columns={columns}
                  dataSource={availableUsers}
                  rowKey={({ user }) => user.id}
                />
              </Card.Grid>
            )}
          </Card>
        </Col>
      </Row>
      <AddUsersDialog
        isDialogOpen={showAddUsersDialog}
        toggleDialog={toggleShowAddUsersDialog}
        onOkClicked={addUsers}
      />
    </>
  );
};
